/**
 * Alert Service
 *
 * Handles CRUD operations for user alerts in Firestore
 * Provides real-time subscriptions to alerts and alert summaries
 *
 * Firestore structure:
 * - clientes/{uid}/alerts/{alertId} - Individual alerts
 * - clientes/{uid}/alerts_summary/current - Aggregated alert counts
 */

import { db } from '../firebase';
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

/**
 * Resolución de ruta org/personal, en UN solo lugar.
 *
 * Espeja `getOrgCollectionPath` del backend (functions/utils/orgHelpers.js), que
 * es quien ESCRIBE estas alertas. Antes el mismo ternario estaba copiado en las
 * seis operaciones de este archivo; que estuvieran todas de acuerdo no evitó el
 * bug real —AlertCenter simplemente no pasaba organizationId y todo caía a la
 * ruta personal, dejando 7 alertas de producción invisibles—. Con un solo punto,
 * romperlo se nota en las seis a la vez, que es justamente lo que la validación
 * por mutación necesita.
 */
const alertsPath = (uid, organizationId) =>
  organizationId ? ['organizations', organizationId, 'alerts'] : ['clientes', uid, 'alerts'];

const summaryPath = (uid, organizationId) =>
  organizationId
    ? ['organizations', organizationId, 'alerts_summary', 'current']
    : ['clientes', uid, 'alerts_summary', 'current'];

export const alertService = {
  /**
   * Subscribe to alerts summary (total, unread, critical counts)
   * @param {string} uid - User ID
   * @param {Function} callback - Called with summary data on updates
   * @param {string} organizationId - Optional organization ID
   * @returns {Function} - Unsubscribe function
   */
  subscribeToSummary(uid, callback, organizationId = null) {
    if (!uid) {
      console.error('[alertService] subscribeToSummary: uid is required');
      return () => {};
    }

    try {
      const summaryRef = doc(db, ...summaryPath(uid, organizationId));

      return onSnapshot(summaryRef,
        (snap) => {
          if (snap.exists()) {
            callback(snap.data());
          } else {
            // Return empty summary if doesn't exist yet
            callback({
              totalCount: 0,
              criticalCount: 0,
              warningCount: 0,
              infoCount: 0,
              unreadCount: 0,
              lastUpdated: null,
              categories: {}
            });
          }
        },
        (error) => {
          console.error('[alertService] Error in subscribeToSummary:', error);
          callback(null);
        }
      );
    } catch (error) {
      console.error('[alertService] subscribeToSummary failed:', error);
      return () => {};
    }
  },

  /**
   * Subscribe to alerts (optionally filtered by severity)
   * @param {string} uid - User ID
   * @param {Object} options - Filter options
   * @param {string} options.severity - 'all', 'critical', 'warning', 'info'
   * @param {number} options.limit - Max number of alerts to return
   * @param {boolean} options.unreadOnly - Only return unread alerts
   * @param {string} options.organizationId - Optional organization ID
   * @param {Function} callback - Called with alerts array on updates
   * @returns {Function} - Unsubscribe function
   */
  subscribeToAlerts(uid, options = {}, callback) {
    if (!uid) {
      console.error('[alertService] subscribeToAlerts: uid is required');
      return () => {};
    }

    const {
      severity = 'all',
      limit = 50,
      unreadOnly = false,
      organizationId = null
    } = options;

    try {
      const alertsRef = collection(db, ...alertsPath(uid, organizationId));

      // ALWAYS filter out dismissed alerts
      // Build query based on filters
      let q;
      if (severity !== 'all' && unreadOnly) {
        // Filter by dismissed + severity + read
        q = query(
          alertsRef,
          where('dismissed', '==', false),
          where('severity', '==', severity),
          where('read', '==', false),
          orderBy('createdAt', 'desc')
        );
      } else if (severity !== 'all') {
        // Filter by dismissed + severity
        q = query(
          alertsRef,
          where('dismissed', '==', false),
          where('severity', '==', severity),
          orderBy('createdAt', 'desc')
        );
      } else if (unreadOnly) {
        // Filter by dismissed + read
        q = query(
          alertsRef,
          where('dismissed', '==', false),
          where('read', '==', false),
          orderBy('createdAt', 'desc')
        );
      } else {
        // Filter by dismissed only
        q = query(
          alertsRef,
          where('dismissed', '==', false),
          orderBy('createdAt', 'desc')
        );
      }

      // Apply limit
      if (limit > 0) {
        q = query(q, firestoreLimit(limit));
      }

      return onSnapshot(q,
        (snapshot) => {
          const alerts = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // Convert Firestore Timestamp to JS Date
            createdAt: doc.data().createdAt?.toDate(),
            expiresAt: doc.data().expiresAt?.toDate()
          }));
          callback(alerts);
        },
        (error) => {
          console.error('[alertService] Error in subscribeToAlerts:', error);
          callback([]);
        }
      );
    } catch (error) {
      console.error('[alertService] subscribeToAlerts failed:', error);
      return () => {};
    }
  },

  /**
   * Mark an alert as read
   * @param {string} uid - User ID
   * @param {string} alertId - Alert ID
   * @param {string} organizationId - Optional organization ID
   * @returns {Promise<void>}
   */
  async markAsRead(uid, alertId, organizationId = null) {
    if (!uid || !alertId) {
      console.error('[alertService] markAsRead: uid and alertId are required');
      return;
    }

    try {
      const alertRef = doc(db, ...alertsPath(uid, organizationId), alertId);
      await updateDoc(alertRef, {
        read: true,
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error('[alertService] markAsRead failed:', error);
      throw error;
    }
  },

  /**
   * Mark all alerts as read
   * @param {string} uid - User ID
   * @param {string} organizationId - Optional organization ID
   * @returns {Promise<void>}
   */
  async markAllAsRead(uid, organizationId = null) {
    if (!uid) {
      console.error('[alertService] markAllAsRead: uid is required');
      return;
    }

    try {
      // This would ideally be done via a Cloud Function for efficiency
      // For now, we'll update the summary to mark all as read
      const summaryRef = doc(db, ...summaryPath(uid, organizationId));
      await updateDoc(summaryRef, {
        unreadCount: 0,
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('[alertService] markAllAsRead failed:', error);
      throw error;
    }
  },

  /**
   * Dismiss an alert (hides it from view)
   * @param {string} uid - User ID
   * @param {string} alertId - Alert ID
   * @param {string} organizationId - Optional organization ID
   * @returns {Promise<void>}
   */
  async dismissAlert(uid, alertId, organizationId = null) {
    if (!uid || !alertId) {
      console.error('[alertService] dismissAlert: uid and alertId are required');
      return;
    }

    try {
      const alertRef = doc(db, ...alertsPath(uid, organizationId), alertId);
      await updateDoc(alertRef, {
        dismissed: true,
        dismissedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('[alertService] dismissAlert failed:', error);
      throw error;
    }
  },

  /**
   * Delete an alert permanently
   * @param {string} uid - User ID
   * @param {string} alertId - Alert ID
   * @param {string} organizationId - Optional organization ID
   * @returns {Promise<void>}
   */
  async deleteAlert(uid, alertId, organizationId = null) {
    if (!uid || !alertId) {
      console.error('[alertService] deleteAlert: uid and alertId are required');
      return;
    }

    try {
      const alertRef = doc(db, ...alertsPath(uid, organizationId), alertId);
      await deleteDoc(alertRef);
    } catch (error) {
      console.error('[alertService] deleteAlert failed:', error);
      throw error;
    }
  },

  /**
   * Create a new alert (for testing or manual alert creation)
   * @param {string} uid - User ID
   * @param {Object} alertData - Alert data
   * @param {string} organizationId - Optional organization ID
   * @returns {Promise<string>} - Created alert ID
   */
  async createAlert(uid, alertData, organizationId = null) {
    if (!uid || !alertData) {
      console.error('[alertService] createAlert: uid and alertData are required');
      return null;
    }

    try {
      const {
        type = 'info',
        severity = 'info',
        category = 'system',
        title,
        message,
        actionRequired = false,
        actionLabel = null,
        actionUrl = null,
        metadata = {}
      } = alertData;

      const alertId = `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const alertRef = doc(db, ...alertsPath(uid, organizationId), alertId);

      // 🚨 IMPORTANT: All resources MUST include organizationId field for Firestore security rules
      const alert = {
        id: alertId,
        type,
        severity,
        category,
        title,
        message,
        actionRequired,
        actionLabel,
        actionUrl,
        metadata,
        read: false,
        dismissed: false,
        createdAt: serverTimestamp(),
        expiresAt: null,
        organizationId: organizationId || null, // ✅ Set org ID or null for personal
      };

      await setDoc(alertRef, alert);
      return alertId;
    } catch (error) {
      console.error('[alertService] createAlert failed:', error);
      throw error;
    }
  }
};

export default alertService;
